// Barcode-Scanner.
//
// Wenn der Browser `BarcodeDetector` mitbringt (Chrome/Android, neuere
// Safari-Versionen), wird der genommen: nativ, deutlich schneller und ohne
// Zusatzcode. Sonst greift die mitgelieferte html5-qrcode-Bibliothek.
import { el, haptic } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'];

function nativeDetector(): BarcodeDetectorConstructor | null {
  const ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor })
    .BarcodeDetector;
  return typeof ctor === 'function' ? ctor : null;
}

export interface ScanOptions {
  title?: string;
  hint?: string;
  /** Mehrere Codes hintereinander scannen (ganzer Einkauf). */
  continuous?: boolean;
  onCode?: (code: string) => void | Promise<void>;
}

/**
 * Zeigt die Kamera und liefert den ersten erkannten Barcode.
 * `null` heißt: abgebrochen oder keine Kamera.
 */
export function scanBarcode(options: ScanOptions = {}): Promise<string | null> {
  const {
    title = 'Barcode scannen',
    hint = 'Barcode in den Rahmen halten',
    continuous = false,
    onCode,
  } = options;

  return new Promise((resolve) => {
    const video = el('video', { class: 'scan-video', playsinline: 'true', muted: 'true' });
    const frame = el('div', { class: 'reader scan-frame' }, video);
    const status = el('p', { class: 'scan-hint muted' }, hint);
    const counter = el('p', { class: 'tiny' });
    const torchBtn = el(
      'button',
      { class: 'btn btn-secondary btn-block', type: 'button', hidden: true },
      [icon('flashlight', { size: 18 }), 'Licht an'],
    );

    const sheet = openSheet(el('div', {}, [frame, status, counter, torchBtn]), {
      title,
      onClose: () => stop(null),
    });

    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    let found = 0;
    let fallbackScanner: { stop: () => Promise<void> } | null = null;

    async function stop(result: string | null): Promise<void> {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(raf);
      if (fallbackScanner) await fallbackScanner.stop().catch(() => {});
      stream?.getTracks().forEach((t) => t.stop());
      resolve(result);
    }

    function handle(raw: string): void {
      const code = raw.replace(/\D/g, '');
      if (!code) return;
      haptic(20);
      found++;
      if (continuous) {
        counter.textContent = `${found} erfasst`;
        void onCode?.(code);
        return;
      }
      void stop(code);
      sheet.close();
    }

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        video.srcObject = stream;
        await video.play();
        setupTorch(stream, torchBtn);
      } catch {
        sheet.close();
        toast('Kamera nicht verfügbar. HTTPS und Kamerarechte prüfen.');
        void stop(null);
        return;
      }

      const Detector = nativeDetector();
      if (Detector) {
        const detector = new Detector({ formats: FORMATS });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        const tick = async (): Promise<void> => {
          if (stopped) return;
          if (video.readyState >= 2 && ctx) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0);
            try {
              const codes = await detector.detect(canvas);
              if (codes.length && codes[0]?.rawValue) handle(codes[0].rawValue);
            } catch {
              /* einzelner Fehlversuch – einfach weiter */
            }
          }
          raf = requestAnimationFrame(() => void tick());
        };
        void tick();
        return;
      }

      // Rückfall: vendorierte Bibliothek (kein CDN, offline-tauglich).
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
      await startFallback();
    })();

    async function startFallback(): Promise<void> {
      const Html5Qrcode = (window as unknown as { Html5Qrcode?: FallbackCtor }).Html5Qrcode;
      if (!Html5Qrcode) {
        sheet.close();
        toast('Scanner konnte nicht geladen werden.');
        void stop(null);
        return;
      }
      frame.replaceChildren(el('div', { id: 'reader-fallback' }));
      const scanner = new Html5Qrcode('reader-fallback', { verbose: false });
      fallbackScanner = scanner;
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (text: string) => handle(text),
          () => {},
        );
      } catch {
        sheet.close();
        toast('Kamera nicht verfügbar.');
        void stop(null);
      }
    }
  });
}

interface FallbackCtor {
  new (
    id: string,
    config: { verbose: boolean },
  ): {
    start(
      camera: { facingMode: string },
      config: { fps: number; qrbox: { width: number; height: number } },
      onSuccess: (text: string) => void,
      onError: () => void,
    ): Promise<void>;
    stop(): Promise<void>;
  };
}

/** Taschenlampe, falls die Kamera sie unterstützt – hilft in der Speisekammer. */
function setupTorch(stream: MediaStream, button: HTMLButtonElement): void {
  const track = stream.getVideoTracks()[0];
  if (!track) return;
  const capabilities = track.getCapabilities?.() as { torch?: boolean } | undefined;
  if (!capabilities?.torch) return;

  button.hidden = false;
  let on = false;
  button.addEventListener('click', () => {
    on = !on;
    void track
      .applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] })
      .then(() => {
        button.replaceChildren(
          icon('flashlight', { size: 18 }),
          document.createTextNode(on ? 'Licht aus' : 'Licht an'),
        );
      })
      .catch(() => {
        button.hidden = true;
      });
  });
}
